import React, { createContext, useContext } from "react";
import { cn } from "@/libs/utils";
import Image from "next/image";
import {
  Body2,
  Heading3,
} from "@/components/webview/games/typograpy/typograpy-webview";

interface BannerContextWebView {
  mobileImage: string;
}

const BannerContextWebView = createContext<BannerContextWebView | null>(null);

const BannerProviderWebView = BannerContextWebView.Provider;

const useBannerContextWebView = () => {
  const context = useContext(BannerContextWebView);
  if (!context) {
    throw new Error(
      "Banner compound components cannot be rendered outside the Banner component",
    );
  }
  return context;
};

interface BannerRootProps {
  mobileImage: string;
  children?: React.ReactNode;
}
function BannerRootWebView({ children, mobileImage }: BannerRootProps) {
  return (
    <BannerProviderWebView
      value={{
        mobileImage,
      }}
    >
      <div className="relative w-full">{children}</div>
    </BannerProviderWebView>
  );
}

interface BannerImageProps {
  className?: string;
}
function BannerImage({ className }: BannerImageProps) {
  const { mobileImage } = useBannerContextWebView();
  return (
    <div className={cn("relative min-h-[240px] w-full", className)}>
      <Image
        src={mobileImage}
        alt={`Mobile Banner Image`}
        quality={80}
        className="absolute rounded-t-lg object-cover"
        loading="eager"
        fill
        priority
      />
    </div>
  );
}

interface ContentProps {
  className?: string;
  children: React.ReactNode;
}
function Content({ className, children }: ContentProps) {
  return (
    <>
      <div className="relative grow overflow-hidden">
        <BlurImage />
        <div
          className={cn(
            "relative flex w-full flex-col items-center justify-center px-4 py-6",
            className,
          )}
        >
          {children}
        </div>
      </div>
    </>
  );
}

interface TitleProps {
  text: string;
  className?: string;
}
function Title({ text, className }: TitleProps) {
  return (
    <Heading3
      className={cn(
        "whitespace-pre-wrap text-center font-700 text-black",
        className,
      )}
    >
      {text}
    </Heading3>
  );
}

interface DescriptionProps {
  text: string;
  className?: string;
}
function Description({ text, className }: DescriptionProps) {
  return (
    <Body2 className={cn("pt-2 text-center text-base text-black", className)}>
      {text}
    </Body2>
  );
}

function BlurImage() {
  const { mobileImage } = useBannerContextWebView();
  return (
    <>
      <Image
        className="absolute w-full rounded-b-lg object-cover"
        alt="Blur"
        src={mobileImage}
        quality={50}
        loading="eager"
        fill
        priority
      />
      <div className="pointer-events-none absolute inset-0 size-full rounded-b-lg bg-white/50 backdrop-blur-2xl transition-transform will-change-transform" />
    </>
  );
}

const GamesBannerWebView = Object.assign(BannerRootWebView, {
  Image: BannerImage,
  Content,
  Title,
  Description,
});

export default GamesBannerWebView;
